/**
 * useBillingAdminBrand
 *
 * Brand context for Billing Admin pages.
 * Persists selected brand in localStorage('admin_billing_brand_id').
 * Auto-selects when admin has exactly 1 brand.
 *
 * @see US1, AC-1 in docs/specifications/2026-03-08-billing-admin-ui.md
 */
import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBrands } from "../services/brandApi";
import type { Brand } from "../types/brand";

const STORAGE_KEY = "admin_billing_brand_id";

export interface UseBillingAdminBrandResult {
  /** Currently selected brand (null = not selected) */
  selectedBrand: Brand | null;
  /** All available brands */
  brands: Brand[];
  /** Whether brands are loading */
  isLoading: boolean;
  /** Whether brands failed to load */
  isError: boolean;
  /** Select a brand and persist in localStorage */
  selectBrand: (brandId: string) => void;
  /** Clear the selected brand */
  clearBrand: () => void;
  /** Whether a brand is required (i.e. not yet selected and admin has multiple brands) */
  isBrandRequired: boolean;
}

/**
 * Hook for billing admin brand context.
 *
 * Business rules:
 * - If admin has exactly 1 brand: auto-select it, no dropdown shown
 * - If admin has multiple brands: show dropdown; block pages until selected
 * - Selected brand persisted in localStorage
 * - Brand change triggers TanStack Query cache invalidation (via key dependency)
 */
export const useBillingAdminBrand = (): UseBillingAdminBrandResult => {
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY);
  });

  const { data: brands = [], isLoading, isError } = useQuery({
    queryKey: ["brands"],
    queryFn: getBrands,
  });

  // Auto-select when exactly 1 brand
  useEffect(() => {
    if (!isLoading && brands.length === 1 && !selectedBrandId) {
      const singleBrandId = brands[0].id;
      setSelectedBrandId(singleBrandId);
      localStorage.setItem(STORAGE_KEY, singleBrandId);
    }
  }, [brands, isLoading, selectedBrandId]);

  // Validate persisted brand still exists in list
  useEffect(() => {
    if (!isLoading && brands.length > 0 && selectedBrandId) {
      const stillExists = brands.some((b) => b.id === selectedBrandId);
      if (!stillExists) {
        setSelectedBrandId(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [brands, isLoading, selectedBrandId]);

  const selectBrand = useCallback((brandId: string) => {
    setSelectedBrandId(brandId);
    localStorage.setItem(STORAGE_KEY, brandId);
  }, []);

  const clearBrand = useCallback(() => {
    setSelectedBrandId(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const selectedBrand =
    brands.find((b) => b.id === selectedBrandId) ?? null;

  // Brand is required when: not loading, multiple brands, and none selected
  const isBrandRequired =
    !isLoading && brands.length > 1 && selectedBrand === null;

  return {
    selectedBrand,
    brands,
    isLoading,
    isError,
    selectBrand,
    clearBrand,
    isBrandRequired,
  };
};
